import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NbIconModule } from '@nebular/theme';
import { Observable, combineLatest, map } from 'rxjs';
import { ShoppingCart } from 'shared';
import { ExclusivesCartService } from '../../../exclusives/exclusives.cart.service';
import { ShopService } from '../../../shop/shop.service';
import { UnifiedCartTab, UnifiedCartUIService } from '../unified-cart-ui.service';
import { ExclusivesTabComponent } from './tabs/exclusives-tab.component';
import { NativeTabComponent } from './tabs/native-tab.component';

@Component({
  selector: 'app-cart-drawer-unified',
  standalone: true,
  imports: [CommonModule, NbIconModule, ExclusivesTabComponent, NativeTabComponent],
  templateUrl: './cart-drawer-unified.component.html',
  styleUrl: './cart-drawer-unified.component.scss'
})
export class CartDrawerUnifiedComponent {
  showExclusivesTab = false;
  showNativeTab = false;

  readonly isOpen$: Observable<boolean>;
  readonly activeTab$: Observable<UnifiedCartTab>;
  readonly nativeCount$: Observable<number>;
  readonly exclusivesCount$: Observable<number>;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly ui: UnifiedCartUIService,
    private readonly shop: ShopService,
    private readonly exclusives: ExclusivesCartService
  ) {
    this.isOpen$ = this.ui.open$();
    this.activeTab$ = this.ui.activeTab$();
    this.nativeCount$ = this.shop
      .activeCart$()
      .pipe(map((cart: ShoppingCart | null | undefined) => sumNativeItems(cart)));
    this.exclusivesCount$ = combineLatest([this.exclusives.activeCart$()]).pipe(
      map(([cart]) => cart?.lines.reduce((n, l) => n + l.quantity, 0) ?? 0)
    );
    this.shop
      .shopSettings$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((settings) => {
        this.showExclusivesTab = !!settings.isExclusivesEnabled;
        this.showNativeTab = !!settings.isShopEnabled;
      });
  }

  selectTab(tab: UnifiedCartTab): void {
    this.ui.setActiveTab(tab);
  }

  close(): void {
    this.ui.close();
  }

  cancelAutoClose(): void {
    this.ui.cancelAutoClose();
  }
}

function sumNativeItems(cart: ShoppingCart | null | undefined): number {
  if (!cart?.items) return 0;
  return cart.items.reduce((n, item) => n + (item.quantity ?? 0), 0);
}
